/**
 * MarkovEngine - Comprehensive Markov chain analysis for reliability engineering
 *
 * Supports:
 * - Steady-state analysis (π·Q = 0)
 * - Transient analysis P(t) = e^(Qt)
 * - Availability calculations
 * - Sensitivity analysis
 * - Integration with RBD/FTA results
 * - Reward models (cost, production rates)
 *
 * Uses pure JavaScript with embedded matrix mathematics (no external dependencies)
 */

/**
 * @typedef {Object} State
 * @property {string} id - Unique state identifier
 * @property {string} label - Human-readable state label
 * @property {string} type - State type: 'operational', 'degraded', 'failed', 'maintenance'
 * @property {number} [outputRate=1.0] - Production capacity as fraction (0-1)
 * @property {boolean} [isAbsorbing=false] - Whether this is an absorbing state
 */

/**
 * @typedef {Object} Transition
 * @property {string} id - Unique transition identifier
 * @property {string} from - Source state id
 * @property {string} to - Destination state id
 * @property {number} rate - Transition rate λ (per unit time, typically per hour)
 * @property {string} [label] - Optional description
 * @property {string} [type] - 'failure', 'repair', 'degradation', 'recovery'
 */

/**
 * @typedef {Object} SteadyStateResult
 * @property {Object<string, number>} pi - State probabilities {stateId: probability}
 * @property {number} availability - Steady-state availability
 * @property {number} MTTF - Mean time to failure (hours)
 * @property {number} expectedCost - Expected cost rate (cost/hour)
 * @property {number} expectedProduction - Expected production rate (units/hour or %)
 * @property {number} convergenceError - Residual error from Gaussian elimination
 */

/**
 * @typedef {Object} TransientResult
 * @property {number[]} times - Time points analyzed
 * @property {Object<string, number[]>} probabilities - State probabilities over time
 * @property {number[]} availability - Availability A(t) over time
 * @property {number[]} production - Expected production over time
 */

export class MarkovEngine {
  /**
   * Create a Markov chain analyzer
   *
   * @param {State[]} states - Array of state definitions
   * @param {Transition[]} transitions - Array of transition definitions
   * @throws {Error} If states or transitions are invalid
   */
  constructor(states, transitions) {
    this.validateInput(states, transitions);

    this.states = states.map(s => ({
      id: s.id,
      label: s.label || s.id,
      type: s.type || 'operational',
      outputRate: s.outputRate !== undefined ? s.outputRate : 1.0,
      isAbsorbing: s.isAbsorbing || false
    }));

    this.transitions = transitions.map(t => ({
      id: t.id,
      from: t.from,
      to: t.to,
      rate: t.rate,
      label: t.label || '',
      type: t.type || 'failure'
    }));

    this.stateMap = new Map(this.states.map(s => [s.id, this.states.indexOf(s)]));
    this.transitionMatrix = null;
    this.steadyState = null;
  }

  /**
   * Validate input states and transitions
   * @private
   */
  validateInput(states, transitions) {
    if (!Array.isArray(states) || states.length === 0) {
      throw new Error('States must be a non-empty array');
    }
    if (!Array.isArray(transitions)) {
      throw new Error('Transitions must be an array');
    }

    const stateIds = new Set(states.map(s => s.id));
    for (const trans of transitions) {
      if (!stateIds.has(trans.from) || !stateIds.has(trans.to)) {
        throw new Error(`Transition ${trans.id}: invalid state reference`);
      }
      if (trans.rate < 0) {
        throw new Error(`Transition ${trans.id}: rate must be non-negative`);
      }
      if (!isFinite(trans.rate)) {
        throw new Error(`Transition ${trans.id}: rate must be finite`);
      }
      if (trans.from === trans.to) {
        throw new Error(`Transition ${trans.id}: self-loop transitions are encoded on the diagonal of Q, not as an explicit transition`);
      }
    }

    // OAV-032 (final): Q is a generator (rate) matrix per IEC 61165:2006 §5.2 —
    // rows must sum to zero, off-diagonal entries non-negative, diagonal
    // entries non-positive. We check every row's outgoing-rate aggregate is
    // finite; the diagonal is computed as -Σ(off-diagonal) when the Q matrix
    // is built. This catches duplicate/conflicting transition definitions.
    const outgoingRowSum = new Map(states.map(s => [s.id, 0]));
    const seenEdges = new Set();
    for (const trans of transitions) {
      const edgeKey = `${trans.from}->${trans.to}`;
      if (seenEdges.has(edgeKey)) {
        throw new Error(`OAV-032: duplicate transition ${edgeKey} — Q matrix cannot have two off-diagonal entries for the same (i,j)`);
      }
      seenEdges.add(edgeKey);
      outgoingRowSum.set(trans.from, outgoingRowSum.get(trans.from) + trans.rate);
    }
    for (const [stateId, sum] of outgoingRowSum.entries()) {
      if (!isFinite(sum) || sum < 0) {
        throw new Error(`OAV-032: state ${stateId} has invalid outgoing-rate sum ${sum}`);
      }
    }
  }

  /**
   * Build the Q (intensity) matrix from transitions
   * Q[i][j] = -λᵢᵢ (diagonal, sum of outgoing rates)
   * Q[i][j] = λᵢⱼ (off-diagonal, transition rates)
   *
   * @returns {number[][]} Q matrix (n × n)
   */
  buildTransitionMatrix() {
    const n = this.states.length;
    const Q = Array(n).fill(null).map(() => Array(n).fill(0));

    // Aggregate transitions: sum all rates between each state pair
    const rateMatrix = {};
    for (let i = 0; i < n; i++) {
      rateMatrix[i] = {};
      for (let j = 0; j < n; j++) {
        rateMatrix[i][j] = 0;
      }
    }

    for (const trans of this.transitions) {
      const i = this.stateMap.get(trans.from);
      const j = this.stateMap.get(trans.to);
      rateMatrix[i][j] += trans.rate;
    }

    // Build Q matrix
    for (let i = 0; i < n; i++) {
      let outgoingRate = 0;
      for (let j = 0; j < n; j++) {
        if (i !== j) {
          Q[i][j] = rateMatrix[i][j];
          outgoingRate += rateMatrix[i][j];
        }
      }
      Q[i][i] = -outgoingRate;
    }

    this.transitionMatrix = Q;
    return Q;
  }

  /**
   * Solve steady-state using Gaussian elimination on π·Q = 0, Σπ = 1
   * Replaces one equation with normalization constraint
   *
   * @returns {SteadyStateResult}
   */
  solveSteadyState() {
    if (!this.transitionMatrix) {
      this.buildTransitionMatrix();
    }

    const n = this.states.length;
    const Q = this.deepCopyMatrix(this.transitionMatrix);

    // Replace last equation with normalization constraint
    for (let j = 0; j < n; j++) {
      Q[n - 1][j] = 1;
    }

    // Augment with RHS = [0, 0, ..., 0, 1]
    const b = Array(n).fill(0);
    b[n - 1] = 1;

    // Gaussian elimination with partial pivoting
    const pi = this.gaussianElimination(Q, b);

    // Ensure non-negative probabilities (numerical artifacts)
    for (let i = 0; i < pi.length; i++) {
      pi[i] = Math.max(0, pi[i]);
    }

    // Renormalize
    const sum = pi.reduce((a, b) => a + b, 0);
    if (sum > 0) {
      for (let i = 0; i < pi.length; i++) {
        pi[i] /= sum;
      }
    }

    // Convert to object
    const piObj = {};
    this.states.forEach((state, i) => {
      piObj[state.id] = pi[i];
    });

    // Compute metrics
    const availability = this.computeAvailabilityFromPi(piObj);
    const mttf = this.computeMTTFFromPi(piObj);
    const expectedCost = this.computeExpectedCostRate(piObj);
    const expectedProduction = this.computeExpectedProductionRate(piObj);

    // Compute convergence error: ||π·Q||
    const piQ = this.multiplyVectorMatrix(pi, this.transitionMatrix);
    const error = Math.sqrt(piQ.reduce((a, v) => a + v * v, 0));

    this.steadyState = {
      pi: piObj,
      availability,
      MTTF: mttf,
      expectedCost,
      expectedProduction,
      convergenceError: error
    };

    return this.steadyState;
  }

  /**
   * Solve transient analysis: P(t) = e^(Qt)
   * Uses matrix exponential via Padé approximation (12/12) for numerical stability
   *
   * @param {number[]} timePoints - Array of time points to evaluate
   * @param {number} [tolerance=1e-8] - Padé approximation tolerance
   * @returns {TransientResult}
   */
  solveTransient(timePoints, tolerance = 1e-8) {
    if (!this.transitionMatrix) {
      this.buildTransitionMatrix();
    }

    const Q = this.transitionMatrix;
    const n = this.states.length;

    const probabilities = {};
    this.states.forEach(s => {
      probabilities[s.id] = [];
    });

    const availability = [];
    const production = [];

    for (const t of timePoints) {
      // Compute e^(Qt) via Padé approximation
      const eQt = this.matrixExponential(Q, t, tolerance);

      // P(t) = e₀ · e^(Qt) where e₀ is initial state vector
      // For initial state distribution, use steady-state as default
      let piInitial;
      if (!this.steadyState) {
        // Default: start in first operational state or first state
        piInitial = Array(n).fill(0);
        const opStateIdx = this.states.findIndex(s => s.type === 'operational');
        piInitial[opStateIdx >= 0 ? opStateIdx : 0] = 1;
      } else {
        piInitial = this.states.map(s => this.steadyState.pi[s.id]);
      }

      // Pt = piInitial · eQt
      const Pt = this.multiplyVectorMatrix(piInitial, eQt);

      this.states.forEach((state, i) => {
        probabilities[state.id].push(Pt[i]);
      });

      availability.push(this.computeAvailabilityFromProbabilities(Pt));
      production.push(this.computeProductionFromProbabilities(Pt));
    }

    return {
      times: timePoints,
      probabilities,
      availability,
      production
    };
  }

  /**
   * Compute availability from state probabilities
   * A = Σ(π_i) for operational/degraded states
   *
   * @private
   * @param {Object|number[]} piOrProbs - State probabilities
   * @returns {number} Availability (0-1)
   */
  computeAvailabilityFromPi(pi) {
    return this.states.reduce((sum, state) => {
      if (state.type === 'operational' || state.type === 'degraded') {
        return sum + pi[state.id];
      }
      return sum;
    }, 0);
  }

  /**
   * Compute availability from probability vector
   * @private
   */
  computeAvailabilityFromProbabilities(probs) {
    let avail = 0;
    this.states.forEach((state, i) => {
      if (state.type === 'operational' || state.type === 'degraded') {
        avail += probs[i];
      }
    });
    return avail;
  }

  /**
   * Compute expected production from probability vector
   * @private
   */
  computeProductionFromProbabilities(probs) {
    let prod = 0;
    this.states.forEach((state, i) => {
      prod += probs[i] * state.outputRate;
    });
    return prod;
  }

  /**
   * Compute MTTF (Mean Time To Failure)
   * From first passage time analysis; approximated from steady-state
   *
   * @private
   * @returns {number} MTTF in hours
   */
  computeMTTFFromPi(pi) {
    // Find failure rate: sum of all transitions into failed states
    let failureRate = 0;
    for (const trans of this.transitions) {
      const toState = this.states.find(s => s.id === trans.to);
      if (toState && toState.type === 'failed') {
        failureRate += trans.rate * pi[trans.from];
      }
    }

    if (failureRate <= 0) return Infinity;
    return 1 / failureRate;
  }

  /**
   * Compute steady-state availability
   * @returns {number} Availability (0-1)
   */
  computeAvailability() {
    if (!this.steadyState) {
      this.solveSteadyState();
    }
    return this.steadyState.availability;
  }

  /**
   * Compute MTTF from steady state
   * @returns {number} MTTF in hours
   */
  computeMTTF() {
    if (!this.steadyState) {
      this.solveSteadyState();
    }
    return this.steadyState.MTTF;
  }

  /**
   * Compute expected cost rate based on state costs
   * @param {Object<string, number>} [costs] - Cost per state {stateId: costPerHour}
   * @returns {number} Expected cost rate (cost/hour)
   */
  computeExpectedCostRate(costsOrPi) {
    const pi = (typeof costsOrPi === 'object' && !Array.isArray(costsOrPi)) ? costsOrPi :
               (this.steadyState ? this.steadyState.pi : null);

    if (!pi) {
      this.solveSteadyState();
      return this.steadyState.expectedCost;
    }

    const costs = this.costs || {};
    let expectedCost = 0;

    for (const state of this.states) {
      const cost = costs[state.id] || 0;
      expectedCost += pi[state.id] * cost;
    }

    return expectedCost;
  }

  /**
   * Compute expected production rate based on output rates
   * @returns {number} Expected production (units/hour or %)
   */
  computeExpectedProductionRate(pi = null) {
    if (!pi) {
      if (!this.steadyState) {
        this.solveSteadyState();
      }
      pi = this.steadyState.pi;
    }

    let expectedProduction = 0;
    for (const state of this.states) {
      expectedProduction += pi[state.id] * state.outputRate;
    }

    return expectedProduction;
  }

  /**
   * Run sensitivity analysis: vary one transition rate and observe impact
   *
   * @param {string} transitionId - Transition to vary
   * @param {number} minMultiplier - Minimum rate multiplier (e.g., 0.1 = 10%)
   * @param {number} maxMultiplier - Maximum rate multiplier (e.g., 10.0 = 1000%)
   * @param {number} [steps=10] - Number of steps to evaluate
   * @returns {Object} Sensitivity analysis results
   */
  runSensitivity(transitionId, minMultiplier = 0.1, maxMultiplier = 10, steps = 10) {
    const baseTrans = this.transitions.find(t => t.id === transitionId);
    if (!baseTrans) {
      throw new Error(`Transition ${transitionId} not found`);
    }

    const baseRate = baseTrans.rate;
    const results = {
      transitionId,
      baseRate,
      multipliers: [],
      availability: [],
      MTTF: [],
      expectedCost: [],
      expectedProduction: []
    };

    const multipliers = this.linspace(minMultiplier, maxMultiplier, steps);

    for (const mult of multipliers) {
      // Temporarily modify transition rate
      baseTrans.rate = baseRate * mult;
      this.transitionMatrix = null; // Invalidate cache
      this.steadyState = null;

      // Solve and record
      const ss = this.solveSteadyState();
      results.multipliers.push(mult);
      results.availability.push(ss.availability);
      results.MTTF.push(ss.MTTF);
      results.expectedCost.push(ss.expectedCost);
      results.expectedProduction.push(ss.expectedProduction);
    }

    // Restore original rate
    baseTrans.rate = baseRate;
    this.transitionMatrix = null;
    this.steadyState = null;

    return results;
  }

  /**
   * Assign costs to states for cost analysis
   * @param {Object<string, number>} costMap - {stateId: costPerHour}
   */
  setCosts(costMap) {
    this.costs = costMap;
    this.steadyState = null; // Invalidate
  }

  /**
   * Import states and transitions from RBD (Reliability Block Diagram) topology
   * Converts series/parallel configurations into Markov states
   *
   * @static
   * @param {Object} topology - RBD topology {type: 'series'|'parallel', components: [...]}
   * @returns {Object} {states, transitions}
   */
  static fromRBDTopology(topology) {
    const states = [];
    const transitions = [];
    let stateId = 0;

    // Map component failure rates to Markov states
    const operationalState = {
      id: 'Op',
      label: 'All Operational',
      type: 'operational',
      outputRate: 1.0
    };
    const failedState = {
      id: 'Failed',
      label: 'System Failed',
      type: 'failed',
      outputRate: 0.0
    };

    states.push(operationalState, failedState);

    if (topology.type === 'series') {
      // Series: any component failure causes system failure
      const totalFailureRate = topology.components.reduce((sum, c) => sum + c.failureRate, 0);
      transitions.push({
        id: 'OpToFail',
        from: 'Op',
        to: 'Failed',
        rate: totalFailureRate,
        type: 'failure'
      });
    } else if (topology.type === 'parallel') {
      // Parallel: all components must fail
      // Simplified: create intermediate degraded states
      const n = topology.components.length;

      for (let i = 1; i < n; i++) {
        const degradedId = `Deg${i}`;
        states.push({
          id: degradedId,
          label: `${i} component(s) failed`,
          type: 'degraded',
          outputRate: (n - i) / n
        });
      }

      // Transitions
      for (let i = 0; i < n - 1; i++) {
        const fromId = i === 0 ? 'Op' : `Deg${i}`;
        const toId = `Deg${i + 1}`;
        transitions.push({
          id: `Trans${i}`,
          from: fromId,
          to: toId,
          rate: topology.components[i].failureRate,
          type: 'failure'
        });
      }

      // Final transition to failed
      const lastDegId = `Deg${n - 1}`;
      transitions.push({
        id: 'LastFail',
        from: lastDegId,
        to: 'Failed',
        rate: topology.components[n - 1].failureRate,
        type: 'failure'
      });
    }

    return { states, transitions };
  }

  /**
   * Import states and transitions from fault tree cut sets
   *
   * @static
   * @param {Object[]} cutSets - Array of cut sets [{events: ['e1', 'e2'], ...}, ...]
   * @param {Object<string, number>} eventProbs - Event failure probabilities
   * @returns {Object} {states, transitions}
   */
  static fromFaultTree(cutSets, eventProbs) {
    const states = [];
    const transitions = [];

    // Top event failure state
    states.push({
      id: 'Ok',
      label: 'Normal Operation',
      type: 'operational',
      outputRate: 1.0
    });

    states.push({
      id: 'TopEvent',
      label: 'Top Event (Failure)',
      type: 'failed',
      outputRate: 0.0
    });

    // Simplified: create transitions for each cut set
    // Each cut set represents a unique failure path
    for (let i = 0; i < cutSets.length; i++) {
      const cutSet = cutSets[i];

      // Minimal cut set probability is product of event probabilities
      let cutSetFailureRate = 1;
      for (const event of cutSet.events || []) {
        const rate = eventProbs[event] || 0;
        cutSetFailureRate *= rate;
      }

      // Add transition for this cut set
      if (cutSetFailureRate > 0) {
        transitions.push({
          id: `CS${i}`,
          from: 'Ok',
          to: 'TopEvent',
          rate: cutSetFailureRate / (1 - cutSetFailureRate + 0.0001), // Convert to rate
          type: 'failure',
          label: `Cut set: ${cutSet.events.join(', ')}`
        });
      }
    }

    // Remove duplicates and consolidate
    const aggregatedRate = transitions.reduce((sum, t) => sum + t.rate, 0);
    transitions.length = 0;
    if (aggregatedRate > 0) {
      transitions.push({
        id: 'FaultTreeFailure',
        from: 'Ok',
        to: 'TopEvent',
        rate: aggregatedRate,
        type: 'failure'
      });
    }

    return { states, transitions };
  }

  /**
   * Get state labels for display
   * @returns {Object<string, string>} {stateId: label}
   */
  getStateLabels() {
    const labels = {};
    this.states.forEach(s => {
      labels[s.id] = s.label;
    });
    return labels;
  }

  /**
   * Export results for persistence or visualization
   * @returns {Object} Complete model and results
   */
  exportResults() {
    if (!this.steadyState) {
      this.solveSteadyState();
    }

    return {
      metadata: {
        timestamp: new Date().toISOString(),
        numStates: this.states.length,
        numTransitions: this.transitions.length
      },
      states: this.states,
      transitions: this.transitions,
      steadyState: this.steadyState,
      transitionMatrix: this.transitionMatrix
    };
  }

  /**
   * Import model from exported data
   * @static
   * @param {Object} data - Exported data object
   * @returns {MarkovEngine} Restored engine
   */
  static importResults(data) {
    const engine = new MarkovEngine(data.states, data.transitions);
    engine.transitionMatrix = data.transitionMatrix;
    return engine;
  }

  // ============================================================================
  // Private matrix mathematics utilities
  // ============================================================================

  /**
   * Gaussian elimination with partial pivoting
   * Solves Ax = b by row reduction
   * @private
   */
  gaussianElimination(A, b, tolerance = 1e-12) {
    const n = A.length;
    const M = Array(n).fill(null).map((_, i) => [
      ...A[i],
      b[i]
    ]);

    // Forward elimination with partial pivoting
    for (let col = 0; col < n; col++) {
      // Find pivot
      let maxRow = col;
      for (let row = col + 1; row < n; row++) {
        if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) {
          maxRow = row;
        }
      }

      // Swap rows
      [M[col], M[maxRow]] = [M[maxRow], M[col]];

      // Check for singular matrix
      if (Math.abs(M[col][col]) < tolerance) {
        console.warn('Matrix near singular; continuing with reduced precision');
      }

      // Eliminate column
      for (let row = col + 1; row < n; row++) {
        const factor = M[row][col] / M[col][col];
        for (let j = col; j <= n; j++) {
          M[row][j] -= factor * M[col][j];
        }
      }
    }

    // Back substitution
    const x = Array(n);
    for (let i = n - 1; i >= 0; i--) {
      x[i] = M[i][n];
      for (let j = i + 1; j < n; j++) {
        x[i] -= M[i][j] * x[j];
      }
      x[i] /= M[i][i];
    }

    return x;
  }

  /**
   * Matrix exponential using Padé approximation (12/12)
   * e^X ≈ (P(X) + Q(X)) / (P(-X) + Q(-X))
   * @private
   */
  matrixExponential(A, t, tolerance = 1e-8) {
    const n = A.length;

    // Scale matrix: At
    const At = A.map(row => row.map(v => v * t));

    // Padé coefficients [1, 3, 12, 60, 120, 120]
    const coeffs = [1, 3, 12, 60, 120, 120];

    // Compute powers of At
    const powers = [this.identityMatrix(n)]; // At^0
    for (let k = 1; k <= 6; k++) {
      powers.push(this.matrixMultiply(powers[k - 1], At));
    }

    // Numerator: sum of odd-indexed terms
    let P = this.scaleMatrix(powers[0], coeffs[0]);
    for (let k = 1; k <= 5; k++) {
      P = this.matrixAdd(P, this.scaleMatrix(powers[2 * k], coeffs[k]));
    }

    // Denominator: sum of even-indexed terms (negated)
    let Q = this.scaleMatrix(powers[1], coeffs[0]);
    for (let k = 1; k <= 5; k++) {
      Q = this.matrixAdd(Q, this.scaleMatrix(powers[2 * k + 1], coeffs[k] * (k % 2 === 0 ? 1 : -1)));
    }

    // Solve (P + Q) · x = (P - Q)
    // e^At = (P + Q)^{-1} · (P - Q)
    const PpQ = this.matrixAdd(P, Q);
    const PmQ = this.matrixSubtract(P, Q);

    return this.gaussianEliminationMatrix(PpQ, PmQ);
  }

  /**
   * Multiply vector by matrix: v · M
   * @private
   */
  multiplyVectorMatrix(v, M) {
    const n = M.length;
    const result = Array(n).fill(0);

    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        result[j] += v[i] * M[i][j];
      }
    }

    return result;
  }

  /**
   * Matrix-matrix multiplication
   * @private
   */
  matrixMultiply(A, B) {
    const n = A.length;
    const result = Array(n).fill(null).map(() => Array(n).fill(0));

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        for (let k = 0; k < n; k++) {
          result[i][j] += A[i][k] * B[k][j];
        }
      }
    }

    return result;
  }

  /**
   * Matrix addition
   * @private
   */
  matrixAdd(A, B) {
    return A.map((row, i) => row.map((v, j) => v + B[i][j]));
  }

  /**
   * Matrix subtraction
   * @private
   */
  matrixSubtract(A, B) {
    return A.map((row, i) => row.map((v, j) => v - B[i][j]));
  }

  /**
   * Scale matrix by scalar
   * @private
   */
  scaleMatrix(M, scalar) {
    return M.map(row => row.map(v => v * scalar));
  }

  /**
   * Identity matrix
   * @private
   */
  identityMatrix(n) {
    const I = Array(n).fill(null).map(() => Array(n).fill(0));
    for (let i = 0; i < n; i++) {
      I[i][i] = 1;
    }
    return I;
  }

  /**
   * Deep copy matrix
   * @private
   */
  deepCopyMatrix(M) {
    return M.map(row => [...row]);
  }

  /**
   * Solve AX = B for X (A^{-1}B)
   * @private
   */
  gaussianEliminationMatrix(A, B) {
    const n = A.length;
    const m = B[0].length;

    // Augment [A | B]
    const M = A.map((row, i) => [...row, ...B[i]]);

    // Forward elimination
    for (let col = 0; col < n; col++) {
      // Pivot
      let maxRow = col;
      for (let row = col + 1; row < n; row++) {
        if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) {
          maxRow = row;
        }
      }
      [M[col], M[maxRow]] = [M[maxRow], M[col]];

      // Eliminate
      for (let row = col + 1; row < n; row++) {
        const factor = M[row][col] / M[col][col];
        for (let j = col; j < n + m; j++) {
          M[row][j] -= factor * M[col][j];
        }
      }
    }

    // Back substitution
    const X = Array(n).fill(null).map(() => Array(m).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = 0; j < m; j++) {
        X[i][j] = M[i][n + j];
        for (let k = i + 1; k < n; k++) {
          X[i][j] -= M[i][k] * X[k][j];
        }
        X[i][j] /= M[i][i];
      }
    }

    return X;
  }

  /**
   * Generate linearly spaced array
   * @private
   */
  linspace(start, end, num) {
    const arr = [];
    const step = (end - start) / (num - 1);
    for (let i = 0; i < num; i++) {
      arr.push(start + i * step);
    }
    return arr;
  }
}
